import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { accentDefaults, type ThemeName } from '../tokens/tokens';

export type ThemePreference = ThemeName | 'system';

export interface ThemeContextValue {
  /** What the user chose; 'system' follows prefers-color-scheme. */
  preference: ThemePreference;
  /** The theme actually applied. */
  resolved: ThemeName;
  /** Per-user accent hue (0-360); Paige blue when unset. */
  accentHue: number;
  /** How much of the theme's accent chroma the person's colour carries (0-1): 0 is a neutral, 1 Paige blue's. */
  accentChroma: number;
  setPreference: (p: ThemePreference) => void;
  setAccentHue: (hue: number) => void;
  /** The person's one accent colour as a hue and a share of chroma (the workbench's Appearance sets it). */
  setAccent: (hue: number, chroma: number) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function systemTheme(): ThemeName {
  if (typeof window === 'undefined' || !window.matchMedia) return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export interface ThemeProviderProps {
  children: ReactNode;
  initialPreference?: ThemePreference;
  initialAccentHue?: number;
  /** The element that receives data-theme and the accent hue; defaults to <html>. */
  target?: HTMLElement | null;
}

const PREFERENCE_KEY = 'paige:theme:v1';

function storedPreference(): ThemePreference | null {
  try {
    const value = window.localStorage.getItem(PREFERENCE_KEY);
    return value === 'light' || value === 'dark' || value === 'system' ? value : null;
  } catch {
    return null;
  }
}

const wrapHue = (hue: number): number => ((hue % 360) + 360) % 360;
const share = (chroma: number): number => Math.min(1, Math.max(0, chroma));

/** The person's light or dark, kept in the browser (a per-viewer convenience). */
function usePreference(initial: ThemePreference | undefined) {
  const [preference, setPreferenceState] = useState<ThemePreference>(
    () => initial ?? storedPreference() ?? 'system',
  );
  const setPreference = useCallback((next: ThemePreference) => {
    setPreferenceState(next);
    try {
      window.localStorage.setItem(PREFERENCE_KEY, next);
    } catch {
      // No storage: the choice lasts for the page.
    }
  }, []);
  return { preference, setPreference };
}

/** The system's own light or dark, followed as it changes. */
function useSystemTheme(): ThemeName {
  const [system, setSystem] = useState<ThemeName>(systemTheme);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (): void => setSystem(mq.matches ? 'dark' : 'light');
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return system;
}

/** The accent as a hue and a share of chroma; Paige blue until the person's colour arrives. */
function useAccent(initialHue: number | undefined) {
  const [accent, setAccentState] = useState<{ hue: number; chroma: number }>({
    hue: initialHue ?? accentDefaults.hue,
    chroma: accentDefaults.chroma,
  });
  const setHue = useCallback(
    (hue: number) => setAccentState((was) => ({ ...was, hue: wrapHue(hue) })),
    [],
  );
  const setAccent = useCallback(
    (hue: number, chroma: number) => setAccentState({ hue: wrapHue(hue), chroma: share(chroma) }),
    [],
  );
  return { accent, setHue, setAccent };
}

export function ThemeProvider(props: ThemeProviderProps): ReactNode {
  const { preference, setPreference } = usePreference(props.initialPreference);
  const { accent, setHue, setAccent } = useAccent(props.initialAccentHue);
  const system = useSystemTheme();
  const resolved = preference === 'system' ? system : preference;

  useEffect(() => {
    const el = props.target ?? (typeof document === 'undefined' ? null : document.documentElement);
    if (!el) return;
    el.setAttribute('data-theme', resolved);
    el.style.setProperty('--paige-accent-hue', String(accent.hue));
    el.style.setProperty('--paige-accent-chroma', String(accent.chroma));
  }, [resolved, accent, props.target]);

  const value = useMemo<ThemeContextValue>(
    () => ({
      preference,
      resolved,
      accentHue: accent.hue,
      accentChroma: accent.chroma,
      setPreference,
      setAccentHue: setHue,
      setAccent,
    }),
    [preference, resolved, accent, setPreference, setHue, setAccent],
  );
  return <ThemeContext.Provider value={value}>{props.children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used inside <ThemeProvider>');
  return ctx;
}
